import { z } from 'zod';

/**
 * Shift Log (#148) — the read-side contract over the `operator_actions` audit
 * trail. The audit trail records every operator action but exposed NO read
 * endpoint; this is the "what changed this shift" feed the transfer-of-command
 * doctrine (§4.8) needs for a hand-over briefing. NO new verb — it reads the
 * existing append-only trail.
 *
 * `include` selects which slice of the trail the feed surfaces:
 *   • `changes` (default) — only state-changing actions (acks, sector edits,
 *     incident/period lifecycle, dispatch, SITREPs, resource lifecycle, …). The
 *     handover briefing wants what *changed*, not where the cursor went.
 *   • `all` — every recorded action, including ambient navigation/inspection
 *     (map pans, layer toggles, detail views, clicks). For a full audit replay.
 */

export const ShiftLogIncludeSchema = z.enum(['changes', 'all']);
export type ShiftLogInclude = z.infer<typeof ShiftLogIncludeSchema>;

/** One audit row, flattened for the feed (verb, target, time, operator). */
export const ShiftLogEntrySchema = z.object({
  id: z.string(),
  action_type: z.string(),
  /** Human-readable verb (snake_case → "Title Case"). */
  verb_label: z.string(),
  operator_id: z.string(),
  session_id: z.string(),
  /** Best-effort target label derived from the action payload (an entity name /
   *  id / layer key), or null when the payload carries no recognisable target. */
  target: z.string().nullable(),
  /** Whether this action mutates state (a "change") vs ambient navigation. */
  is_change: z.boolean(),
  /** Server-stamped action time (ISO-8601). */
  timestamp: z.string(),
});
export type ShiftLogEntry = z.infer<typeof ShiftLogEntrySchema>;

/** A per-verb rollup for the grouped "what changed" summary header. */
export const ShiftLogVerbCountSchema = z.object({
  action_type: z.string(),
  verb_label: z.string(),
  count: z.number().int(),
});
export type ShiftLogVerbCount = z.infer<typeof ShiftLogVerbCountSchema>;

/** The grouped shift-log feed served by GET /api/operator-actions/log and
 *  embedded as the Handover SITREP shift-log summary. */
export const ShiftLogSummarySchema = z.object({
  /** Lower time bound applied (the shift-start anchor), ISO-8601. */
  since: z.string(),
  /** Upper bound applied, ISO-8601 (defaults to now when not requested). */
  until: z.string(),
  include: ShiftLogIncludeSchema,
  /** Total entries returned (after the include filter + limit). */
  count: z.number().int(),
  /** Whether the limit truncated the feed (more rows exist in the window). */
  truncated: z.boolean(),
  /** Distinct operators who acted in the window. */
  operators: z.array(z.string()),
  /** Per-verb rollup, busiest first — the grouped summary. */
  by_verb: z.array(ShiftLogVerbCountSchema),
  /** Newest-first chronological feed. */
  entries: z.array(ShiftLogEntrySchema),
});
export type ShiftLogSummary = z.infer<typeof ShiftLogSummarySchema>;
